import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { PeriodisationFieldProps } from "models/propTypes/incomingInvoicePropTypes";
import { PeriodisationRowsFormValue } from "models/formTypes/incomingInvoice";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
import { DateRange } from "models/formTypes/util";
//#endregion
//#region components
import { Grid } from "@mui/material";
import StyledButton from "../buttons/styledButton";
import StaticInfoMessage from "../messages/staticInfoMessage";
import DialogEvents from "../dialogs/dialogEvents";
import DetailsTableViewSmall from "../detailsTableViewSmall";
import MonthOrDateRangePicker from "../datePickers/monthOrDateRangePicker";
import usePeriodisationMinDate from "../forms/hooks/usePeriodisationMinDate";
import PeriodisationAdvancedDialog from "./periodisationAdvancedDialog";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { AccrualHelper, FormHelper, LocalizationHelperInstance, PeriodisationRowsHelper } from "utils/helpers";

const emptyValue = (): PeriodisationRowsFormValue => ({ mode: "month", isSplit: false, rows: [{}] });

/** The single period field; a split made in the advanced dialog shows read-only in its place. */
const PeriodisationField: React.FC<PeriodisationFieldProps> = (props) => {
  const {
    setValue,
    error,
    hasError,
    netAmount,
    currency,
    disabled,
    splitSupported,
    hideErrorText,
    fileViewer,
    compactSummary,
    invoiceMonth,
    isCredit,
  } = props;
  const value = props.value ?? emptyValue();
  const minDate = usePeriodisationMinDate(value.mode);

  const update = (next: PeriodisationRowsFormValue) => setValue(next, true);

  const setMode = (mode: PeriodisationMode) => update({ mode, isSplit: false, rows: [{}] });
  const setRange = (range: DateRange | undefined) => update({ ...value, isSplit: false, rows: [{ range }] });

  const errorKey = typeof error === "string" ? error : FormHelper.getErrorMessages(error)[0];
  const errorText = hasError && !hideErrorText && errorKey && (
    <div className={cssExports.periodisationRowsError} role="alert">
      {LocalizationHelperInstance.getFormattedMessage(errorKey, errorKey)}
    </div>
  );

  const allInInvoiceMonthText = PeriodisationRowsHelper.isSplitAllInInvoiceMonth(value, invoiceMonth) && (
    <StaticInfoMessage fullWidth>
      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodisationAllInInvoiceMonth)}
    </StaticInfoMessage>
  );

  return (
    <DialogEvents
      render={(isOpen, openDialog, closeDialog, key) => (
        <>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              {value.isSplit ? (
                <div className={clsx(!compactSummary && [cssExports.readOnlyForm, cssExports.detailsTableFitContent])}>
                  <DetailsTableViewSmall
                    detailsList={AccrualHelper.getPeriodisationRowsKeyValueArray(value, netAmount, currency, isCredit)}
                  />
                </div>
              ) : (
                <div className={cssExports.periodisationFillPicker}>
                  <MonthOrDateRangePicker
                    mode={value.mode}
                    onModeChange={setMode}
                    range={value.rows[0]?.range}
                    onRangeChange={setRange}
                    minDate={minDate}
                    disabled={disabled}
                    error={hasError}
                  />
                </div>
              )}
              {errorText}
              {allInInvoiceMonthText}
            </Grid>
            {splitSupported && !disabled && (
              <Grid item>
                <StyledButton size="xs" onClick={openDialog}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Advanced)}
                </StyledButton>
              </Grid>
            )}
          </Grid>
          {splitSupported && !disabled && (
            <PeriodisationAdvancedDialog
              key={key}
              isOpen={isOpen}
              closeDialog={closeDialog}
              value={value}
              netAmount={netAmount}
              currency={currency}
              fileViewer={fileViewer}
              invoiceMonth={invoiceMonth}
              isCredit={isCredit}
              onSave={update}
            />
          )}
        </>
      )}
    />
  );
};

export default PeriodisationField;
